import { useEffect, useMemo, useState } from 'react';

import {
  isAbleToSeeUIElement,
  ToolsRoles,
} from '../../../utils/roles/client-roles';
import { Schedulable, SignatureModel } from '../../../../types';

import { useStore } from '../../state/zustand-store';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { handleErrors } from '../../../utils/errors';
import {
  editSignature,
  getAllSignatures,
  getCustomSchemas,
  getDomains,
  saveSignature,
  UserKeys,
} from '../../../api/users-api';
import {
  Control,
  SubmitHandler,
  useForm,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import * as analytics from '../../../shared/analytics/analytics.json';
import dayjs from 'dayjs';
import { ModuleList } from '../../../shared/analytics/module-list';
import { useAnalytics } from '../../../shared/analytics/create-events';
import SharedScheduleStep from '../../../shared/shared-schedule-step';
import SimpleBreadcrumbsPage from '../../../shared/simple-breadcrumbs-page';
import SimpleFormPage from '../../../shared/simple-form-page';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { pagesConfig } from '../../../shared/pages-config';
import { useUserSettings } from '../../../utils/use-user-settings';
import FormSkeleton from './form-step/form-skeleton';
import StepSignatureTemplateBasicInfo from './form-step/step-signature-template-basic-info';
import StepSignatureTemplateCreator from './form-step/step-signature-template-creator';
import StepSignatureTemplateTargets from './form-step/step-signature-template-targets';
import StepSignatureTemplateReview from './form-step/step-signature-template-review';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import UnsavedChangesDialog, {
  useUnsavedChanges,
} from '../../../shared/unsaved-changes-dialog';

import SharedFormStepper, {
  StepConfig,
} from '../../../shared/shared-form-stepper';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import Box from '@mui/material/Box';

const getDefaultValues = (
  action: string,
  template: SignatureModel
): SignatureTemplateFormValues => {
  const baseValues: SignatureTemplateFormValues = {
    createdDate: dayjs(new Date()).valueOf(),
    lastRunDate: 0,
    templateName: '',
    description: '',
    templateHtml: '',
    manualRun: true,
    scheduled: false,
    runOnDefaultAlias: false,
    runOnPrimaryAlias: true,
    runOnAliases: false,
    useSendAsAccountForTemplate: false,
    sources: [],
    sourceType: 'User',
    emailsToNotify: [],
    notifyViaEmail: false,
    runImmediately: false,
    dates: [],
    times: [],
    daysOfWeek: false,
    loadedSchedule: false,
    allSource: false,
    scheduleEndDate: 0,
  };
  switch (action) {
    case 'create':
      return { ...baseValues };
    case 'update':
      return {
        ...baseValues,
        ...template,
        lastRunDate: template.lastRunDate ? template.lastRunDate : 0,
        runOnDefaultAlias: !!template.runOnDefaultAlias,
        runOnAliases: !!template.runOnAliases,
        runOnPrimaryAlias: template.runOnPrimaryAlias
          ? true
          : !template.runOnDefaultAlias && !template.runOnAliases
          ? true
          : false,
      };

    case 'clone': {
      if (template) {
        return {
          ...baseValues,
          ...template,
          createdDate: dayjs(new Date()).valueOf(),
          lastRunDate: 0,
          templateName: template?.templateName
            ? `${template.templateName}_Clone`
            : '',
          id: undefined,
          runOnDefaultAlias: !!template.runOnDefaultAlias,
          runOnAliases: !!template.runOnAliases,
          runOnPrimaryAlias: template.runOnPrimaryAlias
            ? true
            : !template.runOnDefaultAlias && !template.runOnAliases
            ? true
            : false,
        };
      } else {
        return {
          ...baseValues,
          createdDate: dayjs(new Date()).valueOf(),
          lastRunDate: 0,
          id: undefined,
        };
      }
    }

    default:
      return {
        createdDate: dayjs(new Date()).valueOf(),
        lastRunDate: 0,
        templateName: '',
        description: '',
        templateHtml: '',
        manualRun: true,
        scheduled: false,
        runOnDefaultAlias: false,
        runOnPrimaryAlias: true,
        runOnAliases: false,
        useSendAsAccountForTemplate: false,
        sources: [],
        sourceType: 'User',
        emailsToNotify: [],
        notifyViaEmail: false,
        runImmediately: false,
        dates: [],
        times: [],
        daysOfWeek: false,
        loadedSchedule: false,
        allSource: false,
        scheduleEndDate: 0,
      };
  }
};

const BasicInformationSchema = z
  .object({
    templateName: z.string().min(1, 'Template name is required.'),

    description: z.string().optional(),

    manualRun: z.boolean().catch(false),

    runOnPrimaryAlias: z.boolean().catch(false),
    runOnDefaultAlias: z.boolean().catch(false),
    runOnAliases: z.boolean().catch(false),

    useSendAsAccountForTemplate: z.boolean().catch(false),
    emailsToNotify: z.array(z.string()).or(z.null()).optional(),

    notifyViaEmail: z.boolean(),
    runImmediately: z.boolean(),
  })
  .refine(
    (data) => {
      return (
        data.runOnPrimaryAlias || data.runOnDefaultAlias || data.runOnAliases
      );
    },
    {
      message: 'Please select an option.',
      path: ['runOnPrimaryAlias'],
    }
  );

const TemplateCreatorSchema = z.object({
  templateHtml: z.string().min(1, 'Signature Template is required.'),
});

const TargetSchema = z
  .object({
    sources: z.array(z.string()),
    allSource: z.boolean().catch(false),
    sourceType: z.enum([
      'User',
      'All_Users',
      'All_Groups',
      'Group',
      'OrgUnit',
      'gPanel_Role',
      'Mobile_Device',
      'Domain',
    ]),
  })
  .refine(
    (data) => data.allSource || (data.sources && data.sources.length > 0),
    {
      message: 'Target is required.',
      path: ['sources'],
    }
  );

const ScheduleSchema = z
  .object({
    scheduled: z.boolean().catch(false),
    dates: z.array(z.string()).or(z.null()).optional(),
    times: z.array(z.string()).or(z.null()).optional(),
    scheduleEndDate: z.number().catch(0),
  })
  .superRefine((data, ctx) => {
    if (data.scheduled) {
      if (!data.dates || data.dates.length === 0) {
        ctx.addIssue({
          code: 'custom',
          message: 'At least one date is required.',
          path: ['dates'],
        });
      }
      if (!data.times || data.times.length === 0) {
        ctx.addIssue({
          code: 'custom',
          message: 'At least one time is required.',
          path: ['times'],
        });
      }
    }
  });

export const SignatureTemplateSchema = BasicInformationSchema.and(
  TemplateCreatorSchema
)
  .and(TargetSchema)
  .and(ScheduleSchema)
  .and(
    z.object({
      currentAction: z.enum(['create', 'update', 'clone']).optional(),
      createdDate: z.number(),
      lastRunDate: z.number().nullish(),
      id: z.number().optional(),
      daysOfWeek: z.boolean().catch(false),
      loadedSchedule: z.boolean().catch(false),
    })
  );

export type SignatureTemplateFormValues = z.infer<
  typeof SignatureTemplateSchema
>;

export default function SignatureTemplatesFormPage() {
  const navigate = useNavigate();
  const { changeLogRoles, loggedInUser } = useUserSettings();
  const { id } = useParams();
  const selectedRowId = Number(id);
  const pathname = useLocation().pathname;
  const currentAction = pathname.includes('/create')
    ? 'create'
    : pathname.includes('/edit')
    ? 'update'
    : 'clone';
  const { setSnackbar } = useStore();
  const { createEvent } = useAnalytics();
  const queryClient = useQueryClient();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const [activeStep, setActiveStep] = useState(0);

  const baseSteps: StepConfig[] = [
    {
      label: 'Basic Information',
      schema: BasicInformationSchema,
    },
    {
      label: 'Template Creator',
      schema: TemplateCreatorSchema,
    },
    {
      label: 'Targets',
      schema: TargetSchema,
    },
    {
      label: 'Schedule',
      schema: ScheduleSchema,
    },
    {
      label: 'Review',
    },
  ];

  const {
    data: signatures,
    isLoading: signaturesLoading,
    isFetching: signaturesFetching,
  } = useQuery({
    queryKey: [UserKeys.Signatures],
    queryFn: () => getAllSignatures(),
    meta: {
      errorMessage:
        'There was an issue loading signature templates. Please refresh and try again.',
    },
  });

  const { data: domains } = useQuery({
    queryKey: [UserKeys.Domains],
    queryFn: () => getDomains(),
    meta: {
      errorMessage:
        'There was an issue loading domain data. Please refresh and try again.',
    },
  });

  const signatureData = signatures?.find(
    (signatureTemplate) => signatureTemplate.id === selectedRowId
  );

  const selectedSignatureTemplate = useMemo(() => {
    if (signatureData) {
      return signatureData;
    }

    return {
      createdDate: dayjs().valueOf(),
      lastRunDate: 0,
      templateName: '',
      description: '',
      templateHtml: '',
      manualRun: true,
      scheduled: false,
      runOnDefaultAlias: false,
      runOnPrimaryAlias: true,
      runOnAliases: false,
      useSendAsAccountForTemplate: false,
      sources: [],
      sourceType: 'User' as const,
      emailsToNotify: [],
      notifyViaEmail: false,
      runImmediately: false,
      dates: [],
      times: [],
      daysOfWeek: false,
      loadedSchedule: false,
      scheduleEndDate: 0,
      allSource: false,
      emailsToShare: [],
      id: selectedRowId,
    };
  }, [signatureData, selectedRowId]);

  const { data: customSchemas } = useQuery({
    queryKey: [UserKeys.CustomSchemas],
    queryFn: () => getCustomSchemas(),
  });

  const { mutate, isPending } = useMutation({
    mutationFn: async (data: Partial<SignatureModel>) => {
      switch (currentAction) {
        case 'create':
          return saveSignature(data, changeLogMessage);
        case 'update':
          return editSignature(data, changeLogMessage);
        case 'clone':
          return saveSignature(data, changeLogMessage);
        default:
          throw new Error('Invalid action type');
      }
    },

    onSuccess: (returnData, data) => {
      let successMessage = '';
      switch (currentAction) {
        case 'create':
          successMessage = 'Signature Template added successfully!';
          break;
        case 'update':
          successMessage = 'Signature Template updated successfully!';
          break;
        case 'clone':
          successMessage = 'Signature Template cloned successfully!';
          break;
      }

      setSnackbar({
        open: true,
        severity: 'success',
        message: successMessage,
      });

      const newTemplate = {
        ...(data as SignatureModel),
        id: returnData,
      };

      queryClient.setQueryData(
        [UserKeys.Signatures],
        (oldData: SignatureModel[]) => {
          if (!oldData) return;
          if (currentAction === 'clone' || currentAction === 'create') {
            return [...oldData, newTemplate];
          } else {
            return [
              ...oldData.filter((signature) => signature.id !== data.id),
              newTemplate,
            ];
          }
        }
      );
      setChangeLogMessage('');
      setActiveStep(0);
      reset();
      bypass();
      navigate(`/${pagesConfig.signatureTemplates.link}`);
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue with the operation. Please refresh and try again.',
      });
    },
    onSettled: () => {
      const actionKey =
        `${currentAction}_signature_template` as keyof typeof analytics.automation.signature_templates;
      createEvent(analytics.automation.signature_templates[actionKey], {
        module: ModuleList.modules.automation.name,
        section: ModuleList.modules.automation.sections.signatureTemplates,
      });
      setOpenChangeLogDialog(false);
    },
  });

  const {
    handleSubmit,
    control,
    trigger,
    reset,
    getValues,
    setValue,
    watch,
    clearErrors,
    formState: { errors, isValidating, isSubmitting, isDirty },
  } = useForm<SignatureTemplateFormValues>({
    resolver: zodResolver(SignatureTemplateSchema),
    defaultValues: getDefaultValues(currentAction, selectedSignatureTemplate),
  });

  const {
    showDialog: showUnsavedChangesDialog,
    handleConfirm: handleUnsavedConfirm,
    handleCancel: handleUnsavedCancel,
    bypass,
    isCancelAction,
  } = useUnsavedChanges(isDirty);

  const handleCancel = () => {
    navigate(`/${pagesConfig.signatureTemplates.link}`, {
      state: { fromCancelButton: true },
    });
  };

  const isScheduled = watch('scheduled');

  const steps = isScheduled
    ? baseSteps
    : baseSteps.filter((step) => step.label !== 'Schedule');

  function handleFormData(data: SignatureTemplateFormValues) {
    return {
      ...data,
      dates: watch('scheduled') === false ? [] : watch('dates'),
      times: watch('scheduled') === false ? [] : watch('times'),
    };
  }

  const onSubmit: SubmitHandler<SignatureTemplateFormValues> = (data) => {
    if (
      (isAbleToSeeUIElement(changeLogRoles, ToolsRoles.CreateTemplates.value) &&
        currentAction === 'create') ||
      (isAbleToSeeUIElement(changeLogRoles, ToolsRoles.EditTemplate.value) &&
        currentAction === 'update')
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate(handleFormData(data));
    }
  };

  const isLoading = signaturesLoading || signaturesFetching;

  const [stepCompleted, setStepCompleted] = useState(() =>
    steps.map(() => (selectedRowId ? true : false))
  );

  const formStepperProps = {
    activeStep,
    setActiveStep,
    steps,
    errors,
    trigger,
    stepCompleted,
    setStepCompleted,
  };

  const selectedTemplate = getValues();

  useEffect(() => {
    if (selectedSignatureTemplate) {
      reset(getDefaultValues(currentAction, selectedSignatureTemplate));
    }
  }, [selectedSignatureTemplate, currentAction, reset]);

  useEffect(() => {
    setStepCompleted((prev) => {
      if (prev.length === steps.length) return prev;

      return steps.map((step, idx) => {
        if (idx < prev.length) return prev[idx];
        if (step.label === 'Review') return false;
        return selectedRowId ? true : false;
      });
    });
  }, [steps, selectedRowId]);

  return (
    <SimpleBreadcrumbsPage
      isSubPage
      breadcrumbs={[
        { label: ModuleList.modules.automation.name },
        {
          label: pagesConfig.signatureTemplates.name,
          path: pagesConfig.signatureTemplates.link,
        },
      ]}
    >
      <SimpleFormPage
        title={
          currentAction === 'create'
            ? pagesConfig.createSignatureTemplate.name
            : currentAction === 'update'
            ? pagesConfig.editSignatureTemplate.name
            : pagesConfig.cloneSignatureTemplate.name
        }
        stepper={
          <SharedFormStepper<SignatureTemplateFormValues>
            {...formStepperProps}
          />
        }
        {...formStepperProps}
        content={
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {isLoading && <FormSkeleton />}
            {!isLoading && steps[activeStep].label === 'Basic Information' && (
              <StepSignatureTemplateBasicInfo
                control={control}
                watch={watch}
                setValue={setValue}
                errors={errors}
              />
            )}
            {!isLoading && steps[activeStep].label === 'Template Creator' && (
              <StepSignatureTemplateCreator
                control={control}
                watch={watch}
                setValue={setValue}
                errors={errors}
                customSchemas={customSchemas}
                loggedInUser={loggedInUser}
              />
            )}
            {!isLoading && steps[activeStep].label === 'Targets' && (
              <StepSignatureTemplateTargets
                control={control}
                watch={watch}
                setValue={setValue}
                errors={errors}
                domains={domains ?? []}
                trigger={trigger}
                clearErrors={clearErrors}
              />
            )}
            {!isLoading && steps[activeStep].label === 'Schedule' && (
              <SharedScheduleStep
                control={control as unknown as Control<Schedulable>}
                watch={watch as unknown as UseFormWatch<Schedulable>}
                setValue={setValue as unknown as UseFormSetValue<Schedulable>}
                errors={errors}
                processName="signature template"
              />
            )}
            {!isLoading && steps[activeStep].label === 'Review' && (
              <StepSignatureTemplateReview
                selectedTemplate={selectedTemplate}
                currentAction={currentAction}
              />
            )}
          </Box>
        }
        handleDiscard={handleCancel}
        handleSubmit={handleSubmit(onSubmit)}
        // handleNext={handleNext}
        submitButtonText={
          currentAction === 'create'
            ? pagesConfig.createSignatureTemplate.name
            : currentAction === 'update'
            ? 'Update Signature Template'
            : 'Clone Signature Template'
        }
        isLoadingSubmit={isValidating || isSubmitting || isPending}
        isDisableSubmit={isLoading}
      />

      <UnsavedChangesDialog
        isCancelAction={isCancelAction}
        entityName="Signature Template"
        pageMode={currentAction}
        open={showUnsavedChangesDialog}
        handleConfirm={handleUnsavedConfirm}
        handleClose={handleUnsavedCancel}
      />
      <ChangeLogDialog
        open={openChangeLogDialog}
        handleClose={() => setOpenChangeLogDialog(false)}
        changeLogMessage={changeLogMessage}
        setChangeLogMessage={setChangeLogMessage}
        mutate={() => {
          mutate(handleFormData(getValues()));
        }}
        mutateLoading={isPending}
      />
    </SimpleBreadcrumbsPage>
  );
}
